import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ClipboardList } from 'lucide-react';
import { ProductBrand } from '@/components/brand/ProductBrand';
import { SignOutButton } from '@/components/auth/SignOutButton';
import { cn } from '@/utils/cn';
import { AmazonAdsCoveragePanel } from '@/pages/admin/AmazonAdsCoveragePanel';
import { SellerCoveragePanel } from '@/pages/admin/SellerCoveragePanel';
import { VendorCoveragePanel } from '@/pages/admin/VendorCoveragePanel';

type CoverageTab = 'ads' | 'vendor' | 'seller';

const TABS: { id: CoverageTab; label: string }[] = [
  { id: 'ads', label: 'Amazon Ads' },
  { id: 'vendor', label: 'Vendor' },
  { id: 'seller', label: 'Seller' },
];

export function parseCoverageTab(value: string | null): CoverageTab {
  if (value === 'vendor' || value === 'seller') return value;
  return 'ads';
}

export function AdminReportCoveragePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<CoverageTab>(() => parseCoverageTab(searchParams.get('tab')));

  useEffect(() => {
    setTab(parseCoverageTab(searchParams.get('tab')));
  }, [searchParams]);

  const setTabAndUrl = (next: CoverageTab) => {
    setTab(next);
    const params = new URLSearchParams(searchParams);
    if (next === 'ads') {
      params.delete('tab');
    } else {
      params.set('tab', next);
    }
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="min-h-screen bg-page">
      <header className="bg-surface border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <ProductBrand variant="light" showTagline={false} />
            <div className="hidden sm:flex items-center gap-2 border-l border-border pl-4">
              <ClipboardList className="w-5 h-5 text-brand shrink-0" aria-hidden />
              <h1 className="text-headline-md font-semibold text-foreground">Report coverage</h1>
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <Link to="/admin/account-metrics" className="text-body-sm text-brand hover:underline font-medium">
              Account metrics
            </Link>
            <Link to="/admin/stream-subscriptions" className="text-body-sm text-brand hover:underline font-medium">
              Stream subscriptions
            </Link>
            <Link to="/admin/users" className="text-body-sm text-brand hover:underline font-medium">
              Users
            </Link>
            <Link to="/accounts" className="text-body-sm text-foreground-muted hover:text-foreground">
              Accounts
            </Link>
            <SignOutButton />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-6 flex gap-4 border-b border-border text-sm" role="tablist" aria-label="Report coverage">
          {TABS.map((item) => {
            const selected = tab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={selected}
                className={cn(
                  'border-b-2 px-1 pb-2',
                  selected ? 'border-brand text-brand' : 'border-transparent text-foreground-muted'
                )}
                onClick={() => setTabAndUrl(item.id)}
              >
                {item.label}
              </button>
            );
          })}
        </div>
        {tab === 'vendor' ? (
          <VendorCoveragePanel />
        ) : tab === 'seller' ? (
          <SellerCoveragePanel />
        ) : (
          <AmazonAdsCoveragePanel />
        )}
      </main>
    </div>
  );
}
